import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useGetSet, useLatest, useMountedState, useUpdateEffect } from 'react-use';
import { DialogContent, FormControl, Stack } from '@mui/material';

import { DrugsApi, type DrugFormData } from '@/api/domains/drugs.api';
import { DrugsQueryKeys } from '@/api/domains/drugs.query-keys';
import type { ReceptionFeature } from '@/api/domains/drugs.types';
import { useGlobalErrorStore } from '@/components/dialogs';
import { FormProvider } from '@/components/form-controls';
import { ModalBody } from '@/components/Modal';
import { NomenclatureConfirmationEditDialog } from '@/components/nomenclature/dialogs/NomenclatureConfirmationEditDialog';
import { NomenclatureModalSidebar } from '@/components/nomenclature/modals/NomenclatureModalSidebar';
import type { ReceptionFeaturesAliases } from '@/shared/constants/drugs.constants';
import type { RecordStatusId } from '@/shared/constants/record-status.constants';
import { RECORD_STATUS_ACTIVE } from '@/shared/constants/record-status.constants';
import { useNomenclatureReceptionFeatures } from '@/shared/hooks/useNomenclatureReceptionFeatures';
import type { AfterSubmit } from '@/shared/types';

import { nomenclatureCreateEvent } from '../../events';
import {
  useNomenclatureContextWindows,
  useNomenclatureStoreActions,
  useNomenclatureStorePreferredTab,
} from '../../useNomenclatureStore';
import type { NomenclatureFormProps } from '../NomenclatureForm';
import type { NomenclatureModalVariant } from '../types';
import { ReceptionFeatureItemSkeletons } from './components/ReceptionFeatureItemSkeletons';
import { ReceptionFeaturesFormItem } from './components/ReceptionFeaturesFormItem';

// ----------------------------------------------------------------------

const selfTabName = 'receptionFeatures';

type ReceptionFeaturesFormProps = {
  onClose: VoidFunction;
  drug: NomenclatureFormProps['drug'];
  variant: NomenclatureModalVariant | null;
};

type FormValues = AfterSubmit & {
  reception_features: Record<`receptionFeature-${string}`, ReceptionFeature>;
  record_status_id?: RecordStatusId;
};

// ----------------------------------------------------------------------

const getNamePrefix = (alias: string) => `receptionFeature-${alias}` as const;

const sortReceptionFeatures = (receptionFeatures: ReceptionFeature[]) => {
  const subjectAliasOrder: ReceptionFeaturesAliases[] = [
    'adults',
    'children',
    'pregnants',
    'breastfeeding',
    'allergics',
    'diabetics',
    'drivers',
    'interaction_with_alcohol',
  ];

  return receptionFeatures.sort((a, b) => {
    const indexA = subjectAliasOrder.indexOf(a.subject_alias);
    const indexB = subjectAliasOrder.indexOf(b.subject_alias);

    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });
};

// ----------------------------------------------------------------------

const prepareReceptionFeaturesDefaultValues = (receptionFeatures: ReceptionFeature[]) => {
  const preparedReceptionFeaturesDefaultValues: FormValues['reception_features'] = {};

  receptionFeatures.forEach(receptionFeature => {
    const { subject_alias, level, custom_text, minimum_age, minimum_month } = receptionFeature;

    const namePrefix = getNamePrefix(subject_alias);

    preparedReceptionFeaturesDefaultValues[namePrefix] = {
      subject_alias,
      level,
      minimum_age,
      custom_text,
      minimum_month,
    };
  });

  return preparedReceptionFeaturesDefaultValues;
};

// ----------------------------------------------------------------------

export const ReceptionFeaturesForm = (props: ReceptionFeaturesFormProps) => {
  const { onClose, drug, variant } = props;

  const { receptionFeatures, isReceptionFeaturesFetching } = useNomenclatureReceptionFeatures(drug?.id);

  const preferredTab = useNomenclatureStorePreferredTab();

  const isEditMode = variant === 'edit';

  const isEditAfterCreationMode = variant === 'editAfterCreation';

  const shouldPrefillNomenclatureForm = (isEditMode || isEditAfterCreationMode) && !!drug;

  const sortedReceptionFeatures = sortReceptionFeatures(receptionFeatures);

  const defaultValues = {
    reception_features: prepareReceptionFeaturesDefaultValues(sortedReceptionFeatures),
    record_status_id: shouldPrefillNomenclatureForm ? drug.record_status_id : RECORD_STATUS_ACTIVE,
  };

  const [getIsDefaultValuesInitialized, setIsDefaultValuesInitialized] = useGetSet(receptionFeatures.length > 0);
  const isDefaultValuesInitialized = getIsDefaultValuesInitialized();

  const methods = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues,
  });

  const {
    handleSubmit: wrapHandleSubmit,
    setError,
    reset,
    formState: { isDirty, isSubmitting },
  } = methods;

  const {
    closeConfirmationCreateDialog,
    closeConfirmationEditDialog,
    openConfirmationEditDialog,
    setActiveTab,
    setSelectedRow,
  } = useNomenclatureStoreActions();

  const { isConfirmationCreateDialogVisible, isConfirmationEditDialogVisible } = useNomenclatureContextWindows();
  const latestFormState = useLatest({ isDirty });

  const isMounted = useMountedState();
  const { setGlobalError } = useGlobalErrorStore();
  const queryClient = useQueryClient();

  useUpdateEffect(() => {
    reset(defaultValues);

    const isDefaultValuesInitialized = getIsDefaultValuesInitialized();

    if (!isDefaultValuesInitialized) {
      setIsDefaultValuesInitialized(true);
    }
  }, [receptionFeatures]);

  useUpdateEffect(() => {
    if (preferredTab === selfTabName) {
      return;
    }

    const { isDirty } = latestFormState.current;

    if (isDirty) {
      openConfirmationEditDialog();
    } else {
      setActiveTab(preferredTab);
    }
  }, [preferredTab]);

  const handleSubmit = async (formData: FormValues) => {
    try {
      if (drug && drug.maker && isDirty) {
        const { reception_features, record_status_id } = formData;

        const receptionFeatures = Object.values(reception_features as FormValues['reception_features']);
        const preparedReceptionFeatures = receptionFeatures.map(value => ({
          ...value,
          minimum_age: Number(value.minimum_age),
          minimum_month: Number(value.minimum_month),
        }));

        const preparedDrugFormData: DrugFormData = {
          name: drug.name,
          category_id: drug.category_id,
          maker_id: drug.maker_id,
          measure_unit_id: drug.measure_unit_id,
          vat_id: drug.vat_id,
          maker_name: drug.maker.name,
          country_name: drug.maker.country,
          record_status_id,
        };

        await DrugsApi.updateReceptionFeatures(drug.id, preparedReceptionFeatures);
        const updatedDrug = await DrugsApi.updateDrug(drug.id, preparedDrugFormData);
        queryClient.invalidateQueries(DrugsQueryKeys.refreshCurrentSearch());
        queryClient.invalidateQueries(DrugsQueryKeys.receptionFeatures(drug.id));

        setSelectedRow(updatedDrug);

        if (isEditAfterCreationMode) {
          if (drug) {
            nomenclatureCreateEvent.emit(drug);
          }
        }

        if (isConfirmationCreateDialogVisible) {
          closeConfirmationCreateDialog();
        }

        if (isConfirmationEditDialogVisible) {
          closeConfirmationEditDialog();
        }

        if (preferredTab !== selfTabName) {
          setActiveTab(preferredTab);
        }

        if (preferredTab === selfTabName) {
          if (isDirty) {
            await queryClient.invalidateQueries(DrugsQueryKeys.refreshCurrentSearch());
            await queryClient.invalidateQueries(DrugsQueryKeys.receptionFeatures(drug.id));
          }
          onClose();
        }
      }
    } catch (error) {
      setGlobalError(error);

      if (isMounted()) {
        // @ts-expect-error - error object type issue
        setError('afterSubmit', { ...error, message: error.message });
      }
    }
  };

  const handleNomenclatureConfirmationEditDialogClose = () => {
    if (preferredTab === selfTabName) {
      onClose();
    } else {
      setActiveTab(preferredTab);
    }
  };

  const shouldDisplaySkeletons = isReceptionFeaturesFetching || !isDefaultValuesInitialized;

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit}>
      <ModalBody
        hasTabbar
        sidebar={
          <NomenclatureModalSidebar drug={drug} isEditMode={isEditMode || isEditAfterCreationMode} onCancel={onClose} />
        }
      >
        <DialogContent>
          <Stack spacing={2} py={2}>
            {shouldDisplaySkeletons && <ReceptionFeatureItemSkeletons />}
            {!shouldDisplaySkeletons &&
              sortedReceptionFeatures.map(receptionFeature => (
                <FormControl fullWidth key={receptionFeature.subject_alias}>
                  <ReceptionFeaturesFormItem
                    namePrefix={getNamePrefix(receptionFeature.subject_alias)}
                    receptionFeature={receptionFeature}
                  />
                </FormControl>
              ))}
          </Stack>
        </DialogContent>
      </ModalBody>
      <NomenclatureConfirmationEditDialog
        isLoading={isSubmitting}
        onProceed={wrapHandleSubmit(handleSubmit)}
        onClose={handleNomenclatureConfirmationEditDialogClose}
      />
    </FormProvider>
  );
};
